import React, { useEffect, useState } from "react";
import ToggleSwitch from "../../components/patterns/ToggleSwitch";
import { notificationsService } from "../../services/notifications";

const FREQUENCY_OPTIONS = [
  { value: "DA", label: "Daily" },
  { value: "WE", label: "Weekly" },
  { value: "MO", label: "Monthly" },
];

const Notifications = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    notificationsService.fetchSettings(
      (response) => {
        setSettings(response.data);
        setLoading(false);
      },
      () => {
        setError("Could not load notification settings.");
        setLoading(false);
      }
    );
  }, []);

  const saveField = (patch) => {
    setSaving(true);
    setError(null);
    notificationsService.updateSettings(
      patch,
      (response) => {
        setSettings(response.data);
        setSaving(false);
      },
      () => {
        setError("Could not save notification settings.");
        setSaving(false);
      }
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl">
        <p className="text-sm text-theme-text opacity-60">Loading...</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="max-w-4xl">
        <p className="text-sm text-red-500">{error || "Could not load notification settings."}</p>
      </div>
    );
  }

  const allDisabled = settings.unsubscribe_from_all;
  const showDigestsOn = allDisabled ? false : settings.subscribed_to_digests;
  const showProductOn = allDisabled ? false : settings.subscribed_to_product_updates;

  return (
    <div className="max-w-4xl">
      <div className="space-y-6">
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        <div className={`bg-theme-main rounded-lg border border-theme-secondary p-6 ${allDisabled ? "opacity-50" : ""}`}>
          <h2 className="text-lg font-semibold text-theme-text mb-1">Audience digests</h2>
          <p className="text-sm text-theme-text opacity-80 mb-4">
            Periodic snapshots of highlights from your most active audience
          </p>
          <ToggleSwitch
            checked={showDigestsOn}
            disabled={allDisabled || saving}
            onChange={(value) => saveField({ subscribed_to_digests: value })}
            label="Enable audience digests"
          />
          {showDigestsOn && !allDisabled && (
            <div className="mt-4">
              <p className="text-sm text-theme-text opacity-80 mb-2">Frequency</p>
              <div className="flex flex-wrap gap-2">
                {FREQUENCY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={saving}
                    onClick={() => saveField({ digest_frequency: option.value })}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                      settings.digest_frequency === option.value
                        ? "border-theme-primary bg-theme-primary text-theme-button"
                        : "border-theme-secondary text-theme-text hover:border-theme-primary hover:border-opacity-50"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={`bg-theme-main rounded-lg border border-theme-secondary p-6 ${allDisabled ? "opacity-50" : ""}`}>
          <h2 className="text-lg font-semibold text-theme-text mb-1">Product updates</h2>
          <p className="text-sm text-theme-text opacity-80 mb-4">
            News about new features and improvements
          </p>
          <ToggleSwitch
            checked={showProductOn}
            disabled={allDisabled || saving}
            onChange={(value) => saveField({ subscribed_to_product_updates: value })}
            label="Enable product update emails"
          />
        </div>

        <div className="bg-theme-main rounded-lg border border-theme-secondary p-6">
          <h2 className="text-lg font-semibold text-theme-text mb-1">Unsubscribe from all</h2>
          <p className="text-sm text-theme-text opacity-80 mb-4">
            Stop all MoonScroll emails, including digests and product updates
          </p>
          <ToggleSwitch
            checked={settings.unsubscribe_from_all}
            disabled={saving}
            onChange={(value) => saveField({ unsubscribe_from_all: value })}
            label="Unsubscribe from all emails"
          />
        </div>
      </div>
    </div>
  );
};

export default Notifications;
